import React, { useMemo, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { Cards } from 'src/components';
import { Triangle, TriangleDown } from 'src/utils/mdIcons';
import { CategoryEeccIcon } from 'src/scenes/Project/scenes/Dashboard/components/CategoryEeccIcon';
import { formatArea, formatCondition, formatAdjustedArea, formatPercentage } from 'src/utils/formatters';
import { getTableData } from 'src/scenes/Project/components/ecosystemUtils';


const useStyles = makeStyles(theme => ({
  cellBase: {
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    textAlign: 'center',
  },
  textCell: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.2,
    overflow: 'hidden',
    wordWrap: 'break-word',
    hyphens: 'auto',
  },
  numberCell: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  variationCell: {
    fontWeight: 600,
  },
  variationUp: {
    color: theme.palette.success.main,
  },
  variationDown: {
    color: theme.palette.error.main,
  },
  performanceGain: {
    color: theme.palette.success.main,
  },
  performanceLoss: {
    color: theme.palette.error.main,
  },
  totalRow: {
    fontWeight: 700,
  },
  trendIcon: {
    fontSize: '12px',
  },
}));



const EcosystemInventoryTable = ({
  comparisonYears = [ 2020, 2024 ],
  viewMode = 'total',
  areaType = 'adjustedArea',
  visibleColumns = null, // Array de strings con las columnas a mostrar, null = todas
  ecosystems,
}) => {
  const classes = useStyles();
  const cardsRef = useRef();

  const [ year1, year2 ] = comparisonYears;

  const rawEcosystemData = useMemo(() => getTableData(ecosystems, year1, year2, areaType), [ ecosystems, areaType, year1, year2 ]);

  // Función para agrupar datos por formación
  const groupByFormation = data => {
    // Separar la fila total del resto
    const totalRow = data.find(item => item.isTotal);
    const dataWithoutTotal = data.filter(item => !item.isTotal);

    // Agrupar por formación
    const grouped = dataWithoutTotal.reduce((acc, item) => {
      const formation = item.formation;
      if (!acc[formation]) {
        acc[formation] = {
          ecosystem: formation,
          formation: formation,
          vegetationFloor: '-',
          conservationStatus: item.conservationStatus || '-',
          landUse: '-',
          [`area${year1}`]: 0,
          [`condition${year1}`]: 0,
          [`adjustedArea${year1}`]: 0,
          [`area${year2}`]: 0,
          [`condition${year2}`]: 0,
          [`adjustedArea${year2}`]: 0,
          areaVariation: 0,
          conditionVariation: 0,
          adjustedAreaVariation: 0,
          primaryAreaVariation: 0,
          percentageVariation: 0,
          performance: 'Sin cambio',
          count: 0,
        };
      }

      // Sumar valores
      acc[formation][`area${year1}`] += item[`area${year1}`];
      acc[formation][`area${year2}`] += item[`area${year2}`];
      acc[formation][`adjustedArea${year1}`] += item[`adjustedArea${year1}`];
      acc[formation][`adjustedArea${year2}`] += item[`adjustedArea${year2}`];
      acc[formation].areaVariation += item.areaVariation;
      acc[formation].adjustedAreaVariation += item.adjustedAreaVariation;
      acc[formation].primaryAreaVariation += item.primaryAreaVariation || 0;
      acc[formation].count += 1;

      return acc;
    }, {});

    // Convertir a array y calcular promedios de condición y porcentaje de variación
    const groupedArray = Object.values(grouped).map(group => {
      const avgCondition1 = group[`adjustedArea${year1}`] / group[`area${year1}`] || 0;
      const avgCondition2 = group[`adjustedArea${year2}`] / group[`area${year2}`] || 0;
      const conditionVariation = avgCondition2 - avgCondition1;

      // Calcular variación porcentual según el tipo de área
      const primaryArea1 = areaType === 'area' ? group[`area${year1}`] : group[`adjustedArea${year1}`];
      const primaryArea2 = areaType === 'area' ? group[`area${year2}`] : group[`adjustedArea${year2}`];
      const primaryVariation = areaType === 'area' ? group.areaVariation : group.adjustedAreaVariation;

      const percentageVariation = primaryArea1 !== 0
        ? ((primaryArea2 - primaryArea1) / primaryArea1) * 100
        : 0;

      let performance = 'Sin pérdidas';
      if (primaryVariation > 0) {
        performance = 'Ganancia';
      } else if (primaryVariation < 0) {
        performance = 'Pérdida';
      }

      return {
        ...group,
        [`condition${year1}`]: avgCondition1,
        [`condition${year2}`]: avgCondition2,
        conditionVariation,
        percentageVariation,
        performance,
        areaType,
      };
    });

    // Agregar fila total al final si existe
    if (totalRow) {
      groupedArray.push(totalRow);
    }

    return groupedArray;
  };

  // Función para agrupar datos por piso vegetacional
  const groupByVegetationFloor = data => {
    // Separar la fila total del resto
    const totalRow = data.find(item => item.isTotal);
    const dataWithoutTotal = data.filter(item => !item.isTotal);

    // Agrupar por piso vegetacional
    const grouped = dataWithoutTotal.reduce((acc, item) => {
      const vegetationFloor = item.vegetationFloor;
      if (!acc[vegetationFloor]) {
        acc[vegetationFloor] = {
          ecosystem: vegetationFloor,
          formation: '-',
          vegetationFloor: vegetationFloor,
          conservationStatus: item.conservationStatus || '-',
          landUse: '-',
          [`area${year1}`]: 0,
          [`condition${year1}`]: 0,
          [`adjustedArea${year1}`]: 0,
          [`area${year2}`]: 0,
          [`condition${year2}`]: 0,
          [`adjustedArea${year2}`]: 0,
          areaVariation: 0,
          conditionVariation: 0,
          adjustedAreaVariation: 0,
          percentageVariation: 0,
          performance: 'Sin cambio',
          count: 0,
        };
      }

      // Sumar valores
      acc[vegetationFloor][`area${year1}`] += item[`area${year1}`];
      acc[vegetationFloor][`area${year2}`] += item[`area${year2}`];
      acc[vegetationFloor][`adjustedArea${year1}`] += item[`adjustedArea${year1}`];
      acc[vegetationFloor][`adjustedArea${year2}`] += item[`adjustedArea${year2}`];
      acc[vegetationFloor].areaVariation += item.areaVariation;
      acc[vegetationFloor].adjustedAreaVariation += item.adjustedAreaVariation;
      acc[vegetationFloor].count += 1;

      return acc;
    }, {});

    // Convertir a array y calcular promedios de condición y porcentaje de variación
    const groupedArray = Object.values(grouped).map(group => {
      const avgCondition1 = group[`adjustedArea${year1}`] / group[`area${year1}`] || 0;
      const avgCondition2 = group[`adjustedArea${year2}`] / group[`area${year2}`] || 0;
      const conditionVariation = avgCondition2 - avgCondition1;

      // Calcular variación porcentual según el tipo de área
      const primaryArea1 = areaType === 'area' ? group[`area${year1}`] : group[`adjustedArea${year1}`];
      const primaryArea2 = areaType === 'area' ? group[`area${year2}`] : group[`adjustedArea${year2}`];
      const primaryVariation = areaType === 'area' ? group.areaVariation : group.adjustedAreaVariation;

      const percentageVariation = primaryArea1 !== 0
        ? ((primaryArea2 - primaryArea1) / primaryArea1) * 100
        : 0;

      let performance = 'Sin pérdidas';
      if (primaryVariation > 0) {
        performance = 'Ganancia';
      } else if (primaryVariation < 0) {
        performance = 'Pérdida';
      }

      return {
        ...group,
        [`condition${year1}`]: avgCondition1,
        [`condition${year2}`]: avgCondition2,
        conditionVariation,
        percentageVariation,
        performance,
      };
    });

    // Agregar fila total al final si existe
    if (totalRow) {
      groupedArray.push(totalRow);
    }

    return groupedArray;
  };

  // Función para agrupar datos por uso de suelo
  const groupByLandUse = data => {
    // Separar la fila total del resto
    const totalRow = data.find(item => item.isTotal);
    const dataWithoutTotal = data.filter(item => !item.isTotal);

    // Agrupar por uso de suelo
    const grouped = dataWithoutTotal.reduce((acc, item) => {
      const landUse = item.landUse;
      if (!acc[landUse]) {
        acc[landUse] = {
          ecosystem: landUse,
          formation: '-',
          vegetationFloor: '-',
          conservationStatus: '-',
          landUse: landUse,
          [`area${year1}`]: 0,
          [`condition${year1}`]: 0,
          [`adjustedArea${year1}`]: 0,
          [`area${year2}`]: 0,
          [`condition${year2}`]: 0,
          [`adjustedArea${year2}`]: 0,
          areaVariation: 0,
          conditionVariation: 0,
          adjustedAreaVariation: 0,
          percentageVariation: 0,
          performance: 'Sin cambio',
          count: 0,
        };
      }

      // Sumar valores
      acc[landUse][`area${year1}`] += item[`area${year1}`];
      acc[landUse][`area${year2}`] += item[`area${year2}`];
      acc[landUse][`adjustedArea${year1}`] += item[`adjustedArea${year1}`];
      acc[landUse][`adjustedArea${year2}`] += item[`adjustedArea${year2}`];
      acc[landUse].areaVariation += item.areaVariation;
      acc[landUse].adjustedAreaVariation += item.adjustedAreaVariation;
      acc[landUse].count += 1;

      return acc;
    }, {});

    // Convertir a array y calcular promedios de condición y porcentaje de variación
    const groupedArray = Object.values(grouped).map(group => {
      const avgCondition1 = group[`adjustedArea${year1}`] / group[`area${year1}`] || 0;
      const avgCondition2 = group[`adjustedArea${year2}`] / group[`area${year2}`] || 0;
      const conditionVariation = avgCondition2 - avgCondition1;

      // Calcular variación porcentual según el tipo de área
      const primaryArea1 = areaType === 'area' ? group[`area${year1}`] : group[`adjustedArea${year1}`];
      const primaryArea2 = areaType === 'area' ? group[`area${year2}`] : group[`adjustedArea${year2}`];
      const primaryVariation = areaType === 'area' ? group.areaVariation : group.adjustedAreaVariation;

      const percentageVariation = primaryArea1 !== 0
        ? ((primaryArea2 - primaryArea1) / primaryArea1) * 100
        : 0;

      let performance = 'Sin pérdidas';
      if (primaryVariation > 0) {
        performance = 'Ganancia';
      } else if (primaryVariation < 0) {
        performance = 'Pérdida';
      }

      return {
        ...group,
        [`condition${year1}`]: avgCondition1,
        [`condition${year2}`]: avgCondition2,
        conditionVariation,
        percentageVariation,
        performance,
      };
    });

    // Agregar fila total al final si existe
    if (totalRow) {
      groupedArray.push(totalRow);
    }

    return groupedArray;
  };

  const ecosystemData = viewMode === 'formation'
    ? groupByFormation(rawEcosystemData)
    : viewMode === 'vegetation-floor'
      ? groupByVegetationFloor(rawEcosystemData)
      : viewMode === 'land-use'
        ? groupByLandUse(rawEcosystemData)
        : rawEcosystemData;

  const formatHectares = value => {
    if (typeof value === 'number') {
      return formatArea(value);
    }
    return value;
  };

  const getPerformance = variationPositive => (
    variationPositive > 0 ? 'Ganancia'
    : variationPositive < 0 ? 'Pérdida'
    : 'Sin cambio'
  );

  const columns = [
    {
      id: 'ecosystem', // Identificador para filtrado
      title: viewMode === 'vegetation-floor' ? 'Ecosistemas'
      : viewMode === 'land-use' ? 'Uso de suelo'
      : 'Formación',
      headerTextAlign: 'center',
      width: '200px',
      render: ({ formation, vegetationFloor, landUse, isTotal }) => (
        <Box className={clsx(classes.cellBase, classes.textCell, { [classes.totalRow]: isTotal })}>
          {isTotal ? 'TOTAL'
          : viewMode === 'vegetation-floor' ? vegetationFloor
          : viewMode === 'land-use' ? landUse
          : formation}
        </Box>
      ),
    },
    ...(viewMode === 'vegetation-floor' ? [
      {
        id: 'conservationStatus',
        title: 'Estado de conservación',
        headerTextAlign: 'center',
        width: '180px',
        render: ({ conservationStatus, isTotal }) => (
          <Box className={clsx(classes.cellBase, classes.textCell, { [classes.totalRow]: isTotal })}>
            {conservationStatus && conservationStatus !== '-' ? (
              <CategoryEeccIcon category={conservationStatus} />
            ) : (
              '-'
            )}
          </Box>
        ),
      },
    ] : []),
    ...(viewMode === 'total' ? [
      {
        id: 'vegetationFloor',
        title: 'Ecosistemas',
        headerTextAlign: 'center',
        width: '200px',
        render: ({ vegetationFloor, isTotal }) => (
          <Box className={clsx(classes.cellBase, classes.textCell, { [classes.totalRow]: isTotal })}>
            {vegetationFloor}
          </Box>
        ),
      },
      {
        id: 'conservationStatus',
        title: 'Estado de conservación',
        headerTextAlign: 'center',
        width: '180px',
        render: ({ conservationStatus, isTotal }) => (
          <Box className={clsx(classes.cellBase, classes.textCell, { [classes.totalRow]: isTotal })}>
            {conservationStatus && conservationStatus !== '-' ? (
              <CategoryEeccIcon category={conservationStatus} />
            ) : (
              '-'
            )}
          </Box>
        ),
      },
      {
        id: 'landUse',
        title: 'Uso de suelo',
        headerTextAlign: 'center',
        width: '120px',
        render: ({ landUse, isTotal }) => (
          <Box className={clsx(classes.cellBase, classes.textCell, { [classes.totalRow]: isTotal })}>
            {landUse}
          </Box>
        ),
      },
    ] : []),
    {
      id: `area${year1}`,
      title: `Área [Ha] ${year1}`,
      headerTextAlign: 'center',
      width: '100px',
      render: item => {
        const value = item[`area${year1}`];
        return (
          <Box className={clsx(classes.cellBase, classes.numberCell, { [classes.totalRow]: item.isTotal })}>
            {formatHectares(value)}
          </Box>
        );
      },
    },
    {
      id: `condition${year1}`,
      title: `Condición ${year1}`,
      headerTextAlign: 'center',
      width: '90px',
      render: item => {
        const value = item[`condition${year1}`];
        return (
          <Box className={clsx(classes.cellBase, classes.numberCell, { [classes.totalRow]: item.isTotal })}>
            {formatCondition(value)}
          </Box>
        );
      },
    },
    ...(areaType === 'adjustedArea' ? [
      {
        id: `adjustedArea${year1}`,
        title: `Área ajustada [Ha] ${year1}`,
        headerTextAlign: 'center',
        width: '110px',
        render: item => {
          const value = item[`adjustedArea${year1}`];
          return (
            <Box className={clsx(classes.cellBase, classes.numberCell, { [classes.totalRow]: item.isTotal })}>
              {formatAdjustedArea(value)}
            </Box>
          );
        },
      },
    ] : []),
    {
      id: `area${year2}`,
      title: `Área [Ha] ${year2}`,
      headerTextAlign: 'center',
      width: '100px',
      render: item => {
        const value = item[`area${year2}`];
        return (
          <Box className={clsx(classes.cellBase, classes.numberCell, { [classes.totalRow]: item.isTotal })}>
            {formatHectares(value)}
          </Box>
        );
      },
    },
    {
      id: `condition${year2}`,
      title: `Condición ${year2}`,
      headerTextAlign: 'center',
      width: '90px',
      render: item => {
        const value = item[`condition${year2}`];
        return (
          <Box className={clsx(classes.cellBase, classes.numberCell, { [classes.totalRow]: item.isTotal })}>
            {formatCondition(value)}
          </Box>
        );
      },
    },
    ...(areaType === 'adjustedArea' ? [
      {
        id: `adjustedArea${year2}`,
        title: `Área ajustada [Ha] ${year2}`,
        headerTextAlign: 'center',
        width: '110px',
        render: item => {
          const value = item[`adjustedArea${year2}`];
          return (
            <Box className={clsx(classes.cellBase, classes.numberCell, { [classes.totalRow]: item.isTotal })}>
              {formatAdjustedArea(value)}
            </Box>
          );
        },
      },
    ] : []),
    {
      id: 'variation',
      title: areaType === 'area' ? 'Variación Área [Ha] (%)' : 'Variación Área ajustada [Ha] (%)',
      headerTextAlign: 'center',
      width: '140px',
      render: item => {
        const variation = areaType === 'area' ? item.areaVariation : item.adjustedAreaVariation;
        const percentage = item.percentageVariation;
        const boxClasses = clsx(classes.cellBase, classes.variationCell, { [classes.totalRow]: item.isTotal });
        return (
          <Box className={boxClasses} display="flex" alignItems="center" justifyContent="center">
            {variation > 0 ? <Triangle className={clsx(classes.trendIcon, classes.variationUp)} />
            : variation < 0 ? <TriangleDown className={clsx(classes.trendIcon, classes.variationDown)} />
            : null
            }
            <Typography
              variant="body2"
              className={clsx({
                [classes.variationUp]: variation > 0,
                [classes.variationDown]: variation < 0,
              })}
              component="span"
            >
              &nbsp;{variation >= 0 ? '+' : ''}{formatAdjustedArea(variation)} ({percentage >= 0 ? '+' : ''}{formatPercentage(percentage)}%)
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'performance',
      title: 'Desempeño',
      headerTextAlign: 'center',
      width: '100px',
      render: item => {
        const variation = item.percentageVariation;
        const performance = getPerformance(variation);
        return (
          <Box
            className={clsx(classes.cellBase, classes.textCell, {
              [classes.totalRow]: item.isTotal,
              [classes.performanceGain]: performance === 'Ganancia',
              [classes.performanceLoss]: performance === 'Pérdida',
            })}
          >
            {performance}
          </Box>
        );
      },
    },
  ];

  // Filtrar columnas si se especifica visibleColumns
  const filteredColumns = visibleColumns
    ? columns.filter(col => visibleColumns.includes(col.id))
    : columns;

  return (
    <Cards
      ref={cardsRef}
      values={ecosystemData}
      variant="condensed"
      columns={filteredColumns}
      highlightLastRow
    />
  );
};

EcosystemInventoryTable.propTypes = {
  comparisonYears: PropTypes.arrayOf(PropTypes.number),
  viewMode: PropTypes.oneOf([ 'total', 'formation', 'vegetation-floor', 'land-use' ]),
  areaType: PropTypes.oneOf([ 'area', 'adjustedArea' ]),
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  ecosystems: PropTypes.object.isRequired,
};


export {
  EcosystemInventoryTable,
};
